import { DeleteOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, message, Pagination, Select, Table } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import LoadingBox from "../../Components/LoadingBox";
import { API } from "../../Config";
import PageHeader from "../../Routes/PageHeader";
import NotificationFormModal from "./form";
const { Option } = Select;

function Notification() {
  const navigate = useHistory();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [modalVisible, setModalVisible] = useState(false);

  const limit = 20;

  // Get userId from Redux auth state
  const { user } = useSelector((state) => state.Auth);
  const isAdmin = user?.id == 1;

  // Initialize filter based on user type
  useEffect(() => {
    if (isAdmin) {
      setFilter("all"); // Admin sees all by default
    } else {
      setFilter("unread"); // Regular users see unread by default
    }
  }, [isAdmin]);

  // Fetch notifications using the new API
  const fetchNotifications = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await axios.get(
        API.BASR_URL +
          `notifications?page=${page}&limit=${limit}&userId=${user.id}`
      );
      const { notifications, total } = response.data.data;
      setData(notifications);
      setTotal(total);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(API.BASR_URL + `notifications/${notificationId}`, {
        params: { userId: user.id },
      });
      fetchNotifications();
      message.success("Successfully Deleted !");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete notification");
    }
  };

  const handleModalSuccess = () => {
    fetchNotifications(); // Refresh the notifications list
  };

  // Filter notifications based on selector and user type
  useEffect(() => {
    if (isAdmin) {
      // Admin logic
      if (filter === "read") {
        setFilteredData(data.filter((notification) => !notification.visible));
      } else if (filter === "unread") {
        setFilteredData(data.filter((notification) => notification.visible));
      } else {
        // "all" - show both read and unread
        setFilteredData(data);
      }
    } else {
      // Regular user logic - only unread by default
      if (filter === "read") {
        setFilteredData(data.filter((notification) => !notification.visible));
      } else {
        // Default to unread for regular users
        setFilteredData(data.filter((notification) => notification.visible));
      }
    }
  }, [data, filter, isAdmin]);

  // Fetch notifications when component mounts or page/user changes
  useEffect(() => {
    fetchNotifications();
  }, [page, user]);

  const handleClick = (data) => {
    // Only navigate, no mark as read functionality
    navigate.push(data?.path || "");
  };

  // Table columns configuration
  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "count",
      render: (text, record, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "User Name",
      dataIndex: ["User", "name"], // Assuming User is included from backend
      key: "userName",
      render: (text, record) => {
        // Handle different possible user data structures
        return (
          record.User?.name ||
          record.user?.name ||
          record.userName ||
          "Unknown User"
        );
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt", // Changed from dateTime to createdAt (common in backend)
      key: "date",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Time",
      dataIndex: "createdAt",
      key: "time",
      render: (text) => new Date(text).toLocaleTimeString(),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => text || "Notification",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Status",
      dataIndex: "visible",
      key: "status",
      render: (visible) => (
        <span
          className={visible ? "text-blue-600 font-medium" : "text-gray-500"}
        >
          {visible ? "Unread" : "Read"}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "visible",
      key: "action",
      render: (visible, record) =>
        visible ? (
          <Button
            onClick={() => handleClick(record)}
            className="d-flex align-items-center justify-content-center"
          >
            <EyeOutlined size={20} />
          </Button>
        ) : (
          <Button
            onClick={() => deleteNotification(record.id)}
            className="d-flex align-items-center justify-content-center"
            danger
          >
            <DeleteOutlined size={20} />
          </Button>
        ),
    },
  ];

  // Filter options based on user type
  const getFilterOptions = () => {
    if (isAdmin) {
      return (
        <>
          <Option value="all">All</Option>
          <Option value="unread">Unread</Option>
          <Option value="read">Read</Option>
        </>
      );
    } else {
      return (
        <>
          <Option value="unread">Unread</Option>
          <Option value="read">Read</Option>
        </>
      );
    }
  };

  return (
    <div className="container">
      <PageHeader
        PageName={"Notifications"}
        breadScrum={
          isAdmin ? "Admin / All Notifications" : "User / My Notifications"
        }
      />

      <div className="mb-4 d-flex justify-content-between align-items-center">
        <Select
          value={filter}
          onChange={(value) => setFilter(value)}
          style={{ width: 120 }}
        >
          {getFilterOptions()}
        </Select>

        <div className="d-flex align-items-end gap-3">
          {isAdmin && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setModalVisible(true)}
              >
                Send Notification
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingBox />
      ) : error ? (
        <div className="text-red-500 text-center p-4">{error}</div>
      ) : filteredData.length === 0 ? (
        <Card>No {filter === "all" ? "" : filter} notifications found</Card>
      ) : (
        <div>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                if (record.visible) {
                  handleClick(record);
                }
              },
              className: record.visible ? "cursor-pointer" : "",
            })}
            rowClassName={(record) =>
              record.visible ? "bg-blue-50 hover:bg-blue-100" : "bg-gray-100"
            }
          />
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            onChange={(newPage) => setPage(newPage)}
            showSizeChanger={false}
            style={{ marginTop: 16, textAlign: "right" }}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} notifications`
            }
          />
        </div>
      )}

      <NotificationFormModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}

export default Notification;
